import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'redaction'
import { createSelector } from 'reselect'
import { constants, scrollToElement } from 'common/helpers'
import { isEqual } from 'lodash'
import { parse } from 'query-string'
import actions from 'redux/actions'
import { FormattedMessage, intlMessageShape } from 'sb-react-intl'

import InfiniteScroll from 'components/ui/InfiniteScroll/InfiniteScroll'
import Pagination from 'components/ui/Pagination/Pagination'
import Products from 'components/smartSearch/Products/Products'
import Product from 'components/product/Product/Product'
import SkeletonProductCard from 'components/skeleton/ProductCard/ProductCard'

import messages from './messages'
import s from './ProductsFeed.scss'


class ProductsFeed extends Component {

  static propTypes = {
    items: PropTypes.array,
    limit: PropTypes.number,
    listClassName: PropTypes.string,
    isFetching: PropTypes.bool,
    infiniteScroll: PropTypes.bool,
    injectItems: PropTypes.array,
    onItemButtonClick: PropTypes.func,
    itemButtonTitle: PropTypes.shape(intlMessageShape),
    metadata: PropTypes.shape({
      placement: PropTypes.string.isRequired,
    }).isRequired,
    snowplowMetadata: PropTypes.object,
    addTo: PropTypes.oneOf([ 'queue', 'cart' ]),
    defaultSelectedQuickViewType: PropTypes.string,
    isProductPriceVisible: PropTypes.bool,
    isQuickViewHidden: PropTypes.bool,
    lazyImages: PropTypes.bool,
    id: PropTypes.string,
  }

  static defaultProps = {
    limit: 60, // items count per page
    infiniteScroll: false,
    addTo: 'queue',
    isProductPriceVisible: false,
    isQuickViewHidden: false,
    lazyImages: false,
    id: 'products',
  }

  getItems = () => {
    const { infiniteScroll, items, limit, pageNumber } = this.props

    if (infiniteScroll) {
      return items.slice(0, pageNumber * limit)
    }

    return items.slice((pageNumber - 1) * limit, pageNumber * limit)
  }

  paginate = ({ selected }, scrollToBeginning = true) => {
    const { pathname, pageNumber: oldPageNumber, infiniteScroll } = this.props

    const newPageNumber = selected + 1
    const compensation = -110 - 20 // 110 is the height of queue container

    if (oldPageNumber !== newPageNumber) {
      if (scrollToBeginning) {
        scrollToElement(this.container, true, compensation)
      }

      // timeout 0 required to prevent instantly call IntersectionObserver on change page
      setTimeout(() => {
        const goTo = infiniteScroll ? actions.models.router.replace : actions.models.router.push

        goTo(newPageNumber === 1 ? pathname : `${pathname}?page=${newPageNumber}`)
      }, 0)
    }
  }

  loadMoreItems = (callback) => {
    const { pageNumber } = this.props

    this.paginate({ selected: pageNumber }, false)
    callback()
  }

  setRef = (ref) => {
    this.container = ref
  }

  componentWillReceiveProps({ items: newItems }) {
    const { items: oldItems } = this.props

    if (!isEqual(newItems, oldItems)) {
      this.paginate({ selected: 0 })
    }
  }

  render() {
    const {
      items, limit, isFetching, listClassName, infiniteScroll, pageNumber, injectItems,
      metadata, snowplowMetadata, onItemButtonClick, itemButtonTitle, defaultSelectedQuickViewType,
      isProductPriceVisible, isQuickViewHidden, lazyImages, addTo, id,
    } = this.props

    const visibleItems      = this.getItems()
    const visibleItemCount  = visibleItems.length
    const totalItemCount    = items.length
    const pagesCount        = Math.ceil(totalItemCount / limit)
    const pseudoItems       = Array(8).fill({})

    if (isFetching) {
      return (
        <Products
          className={listClassName}
          items={pseudoItems}
        >
          {() => <SkeletonProductCard />}
        </Products>
      )
    }

    if (visibleItemCount === 0) {
      return (
        <FormattedMessage className={s.empty} tag="div" {...messages.empty} />
      )
    }

    const itemsNode = (
      <Products
        className={listClassName}
        items={visibleItems}
        injectItems={injectItems}
      >
        {
          (item) => (
            <Product
              data={item}
              addTo={addTo}
              defaultSelectedQuickViewType={defaultSelectedQuickViewType}
              buttonTitle={itemButtonTitle}
              lazyImage={lazyImages}
              isQuickViewVisible={!isQuickViewHidden}
              isPriceVisible={isProductPriceVisible}
              metadata={metadata}
              snowplowMetadata={snowplowMetadata}
              dataTestId={`${item.id}`}
              onButtonClick={onItemButtonClick}
            />
          )
        }
      </Products>
    )

    return (
      <div id={id} ref={this.setRef} data-testid="products">
        {
          infiniteScroll ? (
            <InfiniteScroll
              itemCount={visibleItemCount}
              total={totalItemCount}
              loadMore={this.loadMoreItems}
            >
              {itemsNode}
            </InfiniteScroll>
          ) : (
            itemsNode
          )
        }
        {
          !infiniteScroll && pagesCount > 1 && (
            <div className={s.paginationContainer} data-testid={constants.testIds.common.pagination}>
              <Pagination
                forcePage={(pageNumber - 1)}
                pagesCount={pagesCount}
                onClick={this.paginate}
              />
            </div>
          )
        }
      </div>
    )
  }
}

export default connect({
  pathname: 'routing.location.pathname',
  pageNumber: createSelector(
    (state) => state.routing.location.search,
    (search) => {
      const { page } = parse(search)
      const pageNumber = parseInt(page, 10)

      if (pageNumber && !Number.isNaN(pageNumber)) {
        return pageNumber
      }

      return 1
    }
  ),
})(ProductsFeed)
